import { useCallback, useEffect, useMemo, useState } from 'react';
import { ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import CheckinAuswertung, { BEWERTUNG_STIL, KARTE, SignalChip } from '@/components/CheckinAuswertung';
import { CheckinRhythmusKarte } from '@/components/CheckinRhythmusKarte';

/* Check-in-Inbox /coach/checkins (Coach-Cockpit W2.5, 29.10.2026): Inbox · Ausgewertet · Archiv über BEIDE Systeme
   (Ernährungscoaching und P1 Coaching), Karten mit Signalen und Zitat; ein Klick öffnet die Auswertung, „Check-in
   wechseln“ springt zum nächsten in der Inbox. Rechts daneben Rhythmus & Urlaub des Kunden (über den Trainer-Client).
   Sichtregel und Rechnen: backend/coach/checkins.py. Zustand in der Adresse (`checkin`, `stand`).
   Integration W2 (29.10.2026): mit `leadId` dieselbe Inbox für EINEN Kunden (Reiter Check-ins der Kundenakte) — Liste,
   „Check-in wechseln“ und Archivieren bleiben bei diesem Kunden (`?lead=`); `basis` sind die Adressteile der Akte. */

/* Rhythmus & Urlaub über die Trainer-Routen (/api/coach/checkins/…) — ohne Fragebogen (der liegt bei der Ernährung) */
export const rhythmusClient = (coach) => ({
  einstellung: (mid) => coach.get(`/checkins/mitglied/${mid}/einstellung`),
  speichern: (mid, daten) => coach.put(`/checkins/mitglied/${mid}/einstellung`, daten),
  einschieben: (mid) => coach.post('/checkins/einschieben', { mitglied_id: mid }),
  startwerte: (mid, daten) => coach.post('/checkins/startwerte', { mitglied_id: mid, ...daten }),
});

/* Trainer-Client der Auswertung, auf einen Kunden eingeschränkt: hängt `lead` an Auswertung und Archivieren. */
const mitLead = (coach, leadId) => (leadId ? {
  get: (url, cfg = {}) => coach.get(url, { ...cfg, params: { ...(cfg.params || {}), lead: leadId } }),
  post: (url, body, cfg = {}) => coach.post(url, body, url.includes('/medien') ? cfg : { ...cfg, params: { ...(cfg.params || {}), lead: leadId } }),
} : coach);

const STAENDE = [['inbox', 'Inbox'], ['ausgewertet', 'Ausgewertet'], ['archiv', 'Archiv']];
const fehlertext = (e, rest) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);
const tagDe = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' }) : '');

const BEW_TEXT = { auf_kurs: 'Auf Kurs', stabil: 'Stabil', handlungsbedarf: 'Handlungsbedarf' };

function Karte({ k, aktiv, onWahl }) {
  return (
    <button type="button" onClick={() => onWahl(k.id)} data-testid={`checkin-karte-${k.id}`} aria-current={aktiv ? 'true' : undefined}
      className={`min-h-11 w-full rounded-[10px] px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${aktiv ? 'coach-hinweis border-2 border-marke' : 'border border-border hover:bg-muted/50'}`}>
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate font-extrabold">{k.name || 'Mitglied'}</span>
        {k.bewertung && <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold ${BEWERTUNG_STIL[k.bewertung]}`}>{BEW_TEXT[k.bewertung]}</span>}
      </div>
      <div className="text-[13px] text-muted-foreground">{tagDe(k.eingereicht_am)} · {k.titel}{k.woche && !k.eingeschoben ? ` · ${k.woche}` : ''}{k.eingeschoben ? ' · eingeschoben' : ''}</div>
      {k.signale.length > 0 && <div className="mt-1.5 flex flex-wrap gap-1">{k.signale.slice(0, 2).map((s) => <SignalChip key={s.text} s={s} />)}{k.signale.length > 2 && <span className="text-xs text-muted-foreground">+{k.signale.length - 2}</span>}</div>}
      {k.zitat && <p className="mt-1 line-clamp-2 text-[13px] italic text-muted-foreground">„{k.zitat}“</p>}
    </button>
  );
}

export default function CoachCheckins({ coach, checkin, stand: standParam, gehe, leadId = null, basis = null, onGeaendert }) {
  const stand = STAENDE.some(([s]) => s === standParam) ? standParam : 'inbox';
  const ziel = useMemo(() => basis || { ansicht: 'checkins' }, [basis]);
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const laden = useCallback(() => {
    coach.get('/checkins', { params: { stand, ...(leadId ? { lead: leadId } : {}) } }).then((r) => { setD(r.data); setErr(''); })
      .catch((e) => setErr(fehlertext(e, 'Check-ins nicht geladen')));
  }, [coach, stand, leadId]);
  useEffect(() => { setD(null); laden(); }, [laden]);
  const geaendert = () => { laden(); onGeaendert?.(); };

  const rhythmusApi = useMemo(() => rhythmusClient(coach), [coach]);
  const client = useMemo(() => mitLead(coach, leadId), [coach, leadId]);
  const einschieben = useCallback(async (mid) => {
    try { await rhythmusApi.einschieben(mid); toast.success('Check-in eingeschoben – das Mitglied bekommt einen Hinweis in der App'); geaendert(); }
    catch (e) { toast.error(fehlertext(e, 'Das hat nicht geklappt')); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rhythmusApi]);

  const oeffnen = (id) => gehe({ ...ziel, stand, checkin: id });
  const rechts = leadId ? null : (x) => <CheckinRhythmusKarte key={x.mitglied_id} leadId={x.mitglied_id} api={rhythmusApi} />;
  const auswertung = (id) => (
    <CheckinAuswertung key={id} client={client} id={id} onWeiter={oeffnen} onGeaendert={geaendert} rechts={rechts} onEinschieben={leadId ? null : einschieben} />
  );

  /* Kundenakte: Auswahl eines Check-ins öffnet die Auswertung allein (die Akte zeigt die Liste daneben selbst). */
  if (leadId && checkin) {
    return (
      <div className="flex flex-wrap items-start gap-[18px]" data-testid="coach-checkins">
        <button type="button" onClick={() => gehe({ ...ziel, stand })} className="min-h-11 w-full text-left text-sm font-bold text-muted-foreground hover:text-foreground">← Zur Liste</button>
        {auswertung(checkin)}
      </div>
    );
  }

  const liste = d?.checkins || [];
  const aktiv = !leadId ? (checkin || liste[0]?.id || null) : null;
  const chips = (
    <div className="mb-2.5 flex flex-wrap gap-1.5" role="tablist" aria-label="Stand">
      {STAENDE.map(([s, l]) => (
        <button key={s} type="button" role="tab" aria-selected={stand === s} onClick={() => gehe({ ...ziel, stand: s })} data-testid={`checkins-reiter-${s}`}
          className={`min-h-11 rounded-full border px-[11px] py-[5px] text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${stand === s ? 'border-marke bg-marke text-[#140c08]' : 'border-border bg-muted text-foreground hover:bg-muted/70'}`}>
          {l}{d?.zaehler?.[s] > 0 ? ` ${d.zaehler[s]}` : ''}
        </button>
      ))}
    </div>
  );
  const leer = stand === 'inbox' ? (leadId ? 'Keine Check-ins zum Auswerten.' : 'Alles ausgewertet – keine neuen Check-ins.') : stand === 'archiv' ? 'Noch nichts archiviert.' : 'Noch keine ausgewerteten Check-ins.';

  return (
    <div className="flex flex-wrap items-start gap-[18px]" data-testid="coach-checkins">
      <aside className={`${KARTE} !p-3.5 min-w-0 flex-[1_1_260px] ${leadId ? '' : 'md:max-w-[320px]'}`} aria-label="Check-in-Liste">
        {chips}
        {err && <p className="py-4 text-sm text-destructive" role="alert">{err}</p>}
        {!d && !err && <div className="space-y-2"><Skeleton className="h-16 w-full rounded-[10px]" /><Skeleton className="h-16 w-full rounded-[10px]" /></div>}
        {d && liste.length === 0 && (
          <p className="flex flex-col items-center gap-2 py-8 text-center text-sm text-muted-foreground"><ClipboardCheck className="h-6 w-6 text-marke" aria-hidden="true" />{leer}</p>
        )}
        {liste.length > 0 && <div className="flex flex-col gap-2">{liste.map((k) => <Karte key={k.id} k={k} aktiv={k.id === aktiv} onWahl={oeffnen} />)}</div>}
      </aside>
      {aktiv && auswertung(aktiv)}
      {!aktiv && !leadId && d && liste.length === 0 && <div className={`${KARTE} min-w-0 flex-[5_1_560px] py-10 text-center text-sm text-muted-foreground`}>Kein Check-in ausgewählt.</div>}
    </div>
  );
}
